import React, { createContext, useContext, useState, useEffect } from 'react';

const LayoutContext = createContext();

export const LayoutProvider = ({ children }) => {
  // Persist sidebar collapse state in localStorage
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  const [isFocusMode, setIsFocusMode] = useState(false);

  // Sync sidebar collapse state to localStorage
  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => {
      const newVal = !prev;
      localStorage.setItem('sidebar_collapsed', String(newVal));
      return newVal;
    });
  };

  const setSidebarCollapsed = (value) => {
    setIsSidebarCollapsed(value);
    localStorage.setItem('sidebar_collapsed', String(value));
  };

  const toggleFocusMode = () => {
    setIsFocusMode(prev => !prev);
  };

  // Sync focus-mode class on body
  useEffect(() => {
    if (isFocusMode) {
      document.body.classList.add('layout-focus-mode');
    } else {
      document.body.classList.remove('layout-focus-mode');
    }
    return () => {
      document.body.classList.remove('layout-focus-mode');
    };
  }, [isFocusMode]);

  // Sync sidebar collapse class on body
  useEffect(() => {
    if (isSidebarCollapsed) {
      document.body.classList.add('layout-sidebar-collapsed');
    } else {
      document.body.classList.remove('layout-sidebar-collapsed');
    }
    return () => {
      document.body.classList.remove('layout-sidebar-collapsed');
    };
  }, [isSidebarCollapsed]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Toggle focus mode on Ctrl + \ or Ctrl + / or F11 (optional)
      if ((e.ctrlKey && e.key === '\\') || e.key === 'F11') {
        // Only trigger if not typing in an input/textarea
        const activeTag = document.activeElement?.tagName?.toLowerCase();
        if (activeTag !== 'input' && activeTag !== 'textarea') {
          e.preventDefault();
          toggleFocusMode();
        }
      }

      // Exit focus mode on Escape
      if (e.key === 'Escape' || e.key === 'Esc') {
        const activeTag = document.activeElement?.tagName?.toLowerCase();
        if (activeTag !== 'input' && activeTag !== 'textarea') {
          setIsFocusMode(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <LayoutContext.Provider
      value={{
        isSidebarCollapsed,
        toggleSidebar,
        setSidebarCollapsed,
        isFocusMode,
        toggleFocusMode,
        setIsFocusMode
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
